import { Briefcase } from 'lucide-react'
import { useAssignEventVendor } from '@/hooks/useEvents'
import { EventAssignmentRow } from '@/components/EventAssignmentRow'
import { VendorCompanyPicker } from '@/components/VendorCompanyPicker'
import type { EventDetailDto } from '@lib/dto/event.dto'
import { Card } from '@/components/ui/card'

/**
 * The event's planner (docs/planner-vendors.md): one company, whose people see
 * the event as its client does, arrange its floor plan and add documents. One
 * per event, so the picker is offered only while there is none, and only to
 * staff who can edit events.
 */
export function EventPlannerPanel({
  event,
  canEdit,
}: {
  event: EventDetailDto
  /** Whether the viewer can edit events, and so make a company the planner, invite its contact, or take it off. */
  canEdit: boolean
}) {
  const assign = useAssignEventVendor(event.id)

  if (event.planner) {
    return (
      <ul className="divide-y divide-line overflow-hidden rounded-md border border-line">
        <EventAssignmentRow
          event={event}
          assignment={event.planner}
          icon={<Briefcase />}
          removeDescription="Their people lose the event at once, and any floor plan they have open is closed on them. What they changed stays. Their contact and the event’s clients are emailed."
          canEdit={canEdit}
        />
      </ul>
    )
  }

  if (!canEdit) {
    return <p className="rounded-md border border-line px-4 py-3 text-sm text-ink-muted">No planner on this event.</p>
  }

  return (
    <div className="space-y-4">
      <p className="rounded-md border border-line px-4 py-3 text-sm text-ink-muted">
        No planner on this event. Add the company planning it, and its people can see the event and arrange its floor
        plan.
      </p>
      <Card className="p-4">
        <VendorCompanyPicker
          source={{ kind: 'venue', venueId: event.venueId }}
          service="planning"
          idPrefix="event-planner"
          addLabel="Make planner"
          onChoose={async (input) => {
            await assign.mutateAsync(input)
          }}
        />
      </Card>
    </div>
  )
}
